import React, { useEffect } from 'react';
import Immutable from 'immutable';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import {
  UI_OPERATION_STATUS_ERROR,
  UI_OPERATION_STATUS_SUCCESS,
  userGetCurrent,
} from '@uncinc/react-drupal-core';
import { ImmutableRootState } from '@uncinc/react-drupal-core/types';

import { Container } from '@uncinc/uncinc-react-kitchen-sink';

import { Main } from '../../components';
import { LANGUAGES } from '../../config/constants';
import Page from '../../containers/Page';
import useClasses from '../../hooks/useClasses';
import useOperationStatus from '../../hooks/useOperationStatus';
import useTypedDispatch from '../../hooks/useTypedDispatch';
import { validateEmail } from '../../store/actions/User';

import './style.scss';

const ValidateEmailContent: React.FC = () => {
  const dispatch = useTypedDispatch();
  const { buildClass } = useClasses('ValidateEmail');
  const { t } = useTranslation();
  const operationStatus = useOperationStatus('user', 'validate_email');

  const currentPath = useSelector((state: Immutable.Map<string, string>) => state.getIn(['router', 'location', 'pathname'], '') as string);
  const { uid, timestamp, hash } = getOneTimeLoginParams(currentPath);

  const title = t('pages.validate_email.title');

  useEffect(() => {
    if (operationStatus === UI_OPERATION_STATUS_SUCCESS) {
      dispatch(userGetCurrent());
    }
  }, [operationStatus]);

  useEffect(() => {
    if (uid && timestamp && hash) {
      dispatch(validateEmail(uid, timestamp, hash));
    }
  }, [uid, timestamp, hash]);

  return (
    <Main className="col-md-8 offset-md-2 py-8 py-lg-12">
      <h1 className={buildClass('title')}>{title}</h1>
      <p className={buildClass('body')}>
        { operationStatus === UI_OPERATION_STATUS_SUCCESS && t('pages.validate_email.success.body') }
        { operationStatus === UI_OPERATION_STATUS_ERROR && t('pages.validate_email.error.body') }
      </p>
    </Main>
  );
};

const ValidateEmail: React.FC = () => {
  const isLoggedIn = useSelector<ImmutableRootState<boolean>, boolean>((state) => state.getIn(['login', 'loggedIn'], null));

  if (isLoggedIn) {
    return (
      <Page>
        <ValidateEmailContent />
      </Page>
    );
  }

  return (
    <Page>
      <Container>
        <div className="row">
          <ValidateEmailContent />
        </div>
      </Container>
    </Page>
  );
};

function getOneTimeLoginParams(path: string) {
  const languagePrefixes = Object.keys(LANGUAGES);
  const pathIncludesLanguagePrefix = languagePrefixes.some((prefix) => path.startsWith(`/${prefix}`));
  const splitPath = path.split('/').slice(pathIncludesLanguagePrefix ? 3 : 2);

  return {
    uid: parseInt(splitPath[0], 10),
    timestamp: parseInt(splitPath[1], 10),
    hash: splitPath[2],
  };
}

export default ValidateEmail;
